import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import {
  ADMIN_DATE_PLACEHOLDER,
  adminEnumLabel,
  adminOwnerPrimaryLabel,
  formatAdminFinancialValue,
} from './adminPropertyDisplay';

const FINANCING_DISPLAY_KEYS = [
  'financingType',
  'mortgageRate',
  'amortization',
  'term',
  'aphPoints',
  'debtCoverageRatio',
  'qualificationRate',
  'financingCapRate',
  'assumedLoanBalance',
  'assumptionStatedMonthlyPayment',
  'assumptionTermEnd',
  'originationFee',
  'originationFeeType',
  'ltvRatio',
];

const EXTRA_PARAM_KEYS = [
  'refinanceYears',
  'marketValue',
  'revenueGrowthPct',
  'expenseGrowthPct',
  'valueAppreciationPct',
];

function Field({ label, children }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">{label}</dt>
      <dd className="mt-0.5 break-words text-sm text-slate-900">{children}</dd>
    </div>
  );
}

function hasAnyValue(source) {
  if (!source || typeof source !== 'object') return false;
  return Object.values(source).some((value) => value != null && value !== '');
}

function SummaryFields({ keys, source, t, language, prefix = 'admin.properties.field' }) {
  if (!hasAnyValue(source)) return null;
  return (
    <dl className="mt-2 grid gap-3 sm:grid-cols-2">
      {keys.map((key) => {
        const raw = source?.[key];
        if (raw == null || raw === '') return null;
        let display = formatAdminFinancialValue(key, raw, language);
        if (key === 'financingType' && raw) {
          display = adminEnumLabel('admin.properties.financingType', raw, t);
        }
        return (
          <Field key={key} label={t(`${prefix}.${key}`)}>
            {display}
          </Field>
        );
      })}
    </dl>
  );
}

function ScenarioIdentity({ scenario, ownerUid, t }) {
  const creatorUid = scenario.createdBy?.uid || scenario.createdByUid || '';
  const showCreator = Boolean(creatorUid && creatorUid !== ownerUid);
  const creatorLabel = adminOwnerPrimaryLabel(scenario.createdBy || { uid: creatorUid });
  return (
    <>
      <p className="break-all text-xs text-slate-500">
        {t('admin.properties.scenarios.id')}: {scenario.id}
      </p>
      {showCreator ? (
        <p className="text-xs text-slate-500">
          {t('admin.properties.scenarios.createdBy')}:{' '}
          <Link
            to={`/admin/users/${encodeURIComponent(creatorUid)}`}
            className="font-medium text-indigo-600 hover:underline focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            {creatorLabel}
          </Link>
        </p>
      ) : null}
      {scenario.parentScenarioId ? (
        <p className="break-all text-xs text-slate-500">
          {t('admin.properties.scenarios.parent')}: {scenario.parentScenarioId}
        </p>
      ) : null}
    </>
  );
}

function ScenarioParams({ scenario, t, language, showFinancials }) {
  if (!showFinancials || scenario.financing == null) {
    return (
      <p className="mt-2 text-xs text-slate-500">{t('admin.properties.scenarios.financialsHidden')}</p>
    );
  }
  return (
    <>
      <SummaryFields keys={FINANCING_DISPLAY_KEYS} source={scenario.financing} t={t} language={language} />
      <SummaryFields keys={EXTRA_PARAM_KEYS} source={scenario} t={t} language={language} />
      {hasAnyValue(scenario.acquisitionCostsSummary) ? (
        <div className="mt-3">
          <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-600">
            {t('admin.properties.scenarios.acquisitionCosts')}
          </h4>
          <SummaryFields keys={Object.keys(scenario.acquisitionCostsSummary)} source={scenario.acquisitionCostsSummary} t={t} language={language} />
        </div>
      ) : null}
      {hasAnyValue(scenario.revenueSummary) ? (
        <div className="mt-3">
          <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-600">
            {t('admin.properties.scenarios.revenue')}
          </h4>
          <SummaryFields keys={Object.keys(scenario.revenueSummary)} source={scenario.revenueSummary} t={t} language={language} />
        </div>
      ) : null}
      {hasAnyValue(scenario.operatingExpensesSummary) ? (
        <div className="mt-3">
          <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-600">
            {t('admin.properties.scenarios.operatingExpenses')}
          </h4>
          <SummaryFields keys={Object.keys(scenario.operatingExpensesSummary)} source={scenario.operatingExpensesSummary} t={t} language={language} />
        </div>
      ) : null}
    </>
  );
}

function buildScenarioPreview(scenario, t, language, showFinancials) {
  if (!showFinancials || scenario?.financing == null) return '';
  const financing = scenario.financing;
  const parts = [];
  if (financing.financingType) {
    parts.push(adminEnumLabel('admin.properties.financingType', financing.financingType, t));
  }
  if (financing.mortgageRate != null && financing.mortgageRate !== '') {
    parts.push(formatAdminFinancialValue('mortgageRate', financing.mortgageRate, language));
  }
  if (financing.amortization != null && financing.amortization !== '') {
    parts.push(
      t('admin.properties.scenarios.preview.amort').replace(
        '{n}',
        formatAdminFinancialValue('amortization', financing.amortization, language),
      ),
    );
  }
  if (financing.term != null && financing.term !== '') {
    parts.push(
      t('admin.properties.scenarios.preview.term').replace(
        '{n}',
        formatAdminFinancialValue('term', financing.term, language),
      ),
    );
  }
  return parts.join(' · ');
}

function CollapsibleScenario({
  scenario,
  ownerUid,
  t,
  language,
  showFinancials,
  kindLabel,
  nested = false,
}) {
  const children = Array.isArray(scenario.children) ? scenario.children : [];
  const preview = buildScenarioPreview(scenario, t, language, showFinancials);
  const typeLabel = adminEnumLabel('admin.properties.scenarioType', scenario.type, t);
  const subtitle = [kindLabel, typeLabel, preview].filter(Boolean).join(' · ');

  return (
    <details className={`group ${nested ? 'rounded-lg border border-slate-200 bg-slate-50' : 'rounded-xl border border-slate-200 bg-white'}`}>
      <summary className="flex cursor-pointer list-none items-center gap-3 px-4 py-3 marker:content-none [&::-webkit-details-marker]:hidden">
        <ChevronDown
          className="h-4 w-4 shrink-0 text-slate-400 transition-transform group-open:rotate-180"
          aria-hidden="true"
        />
        <div className="min-w-0 flex-1">
          <p className="truncate font-medium text-slate-900">{scenario.title || ADMIN_DATE_PLACEHOLDER}</p>
          {subtitle ? <p className="truncate text-xs text-slate-500">{subtitle}</p> : null}
        </div>
        {children.length > 0 ? (
          <span className="shrink-0 rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-600">
            {t('admin.properties.scenarios.childCount').replace('{count}', String(children.length))}
          </span>
        ) : null}
      </summary>
      <div className="space-y-3 border-t border-slate-100 px-4 py-3">
        <ScenarioIdentity scenario={scenario} ownerUid={ownerUid} t={t} />
        <ScenarioParams scenario={scenario} t={t} language={language} showFinancials={showFinancials} />
        {!nested ? (
          <div>
            <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-600">
              {t('admin.properties.scenarios.subScenarios')}
            </h3>
            {children.length === 0 ? (
              <p className="mt-2 text-sm text-slate-500">{t('admin.properties.scenarios.noSubScenarios')}</p>
            ) : (
              <ul className="mt-2 space-y-2">
                {children.map((child) => (
                  <li key={child.id}>
                    <CollapsibleScenario
                      scenario={child}
                      ownerUid={ownerUid}
                      t={t}
                      language={language}
                      showFinancials={showFinancials}
                      nested
                    />
                  </li>
                ))}
              </ul>
            )}
          </div>
        ) : null}
      </div>
    </details>
  );
}

export default function AdminPropertyScenarioTree({ scenarios, ownerUid, t, language, showFinancials }) {
  const roots = Array.isArray(scenarios?.roots) ? scenarios.roots : [];
  const orphaned = Array.isArray(scenarios?.orphaned) ? scenarios.orphaned : [];
  const returnProjections = Array.isArray(scenarios?.returnProjections)
    ? scenarios.returnProjections
    : [];
  const financingRootCount = Number(scenarios?.financingRootCount) || 0;
  const truncated = Boolean(scenarios?.truncated);
  const totalCount = Number(scenarios?.totalCount) || 0;
  const fetchedCount = Number(scenarios?.fetchedCount) || roots.length;

  return (
    <div className="space-y-4">
      <section className="rounded-xl border border-slate-200 bg-white p-4">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h2 className="text-sm font-semibold text-slate-800">
            {t('admin.properties.section.financingScenarios')}
          </h2>
          {financingRootCount > 0 ? (
            <p className="text-xs text-slate-500">
              {t('admin.properties.scenarios.total').replace('{count}', String(financingRootCount))}
            </p>
          ) : null}
        </div>
        {truncated ? (
          <p className="mt-1 text-sm text-slate-500">
            {t('admin.properties.scenarios.truncated')
              .replace('{fetched}', String(fetchedCount))
              .replace('{total}', String(totalCount))}
          </p>
        ) : null}
        {financingRootCount === 0 ? (
          <p className="mt-3 text-sm text-slate-600">{t('admin.properties.scenarios.financingEmpty')}</p>
        ) : (
          <div className="mt-3 space-y-2">
            {roots.map((root) => (
              <CollapsibleScenario
                key={root.id}
                scenario={root}
                ownerUid={ownerUid}
                t={t}
                language={language}
                showFinancials={showFinancials}
                kindLabel={t('admin.properties.scenarios.kind.initial')}
              />
            ))}
          </div>
        )}
        {Number(scenarios?.returnProjectionCount) > 0 ? (
          <p className="mt-3 text-xs text-slate-500">
            {t('admin.properties.scenarios.returnProjectionCount').replace(
              '{count}',
              String(scenarios.returnProjectionCount),
            )}
          </p>
        ) : null}
        {returnProjections.length > 0 ? (
          <ul className="mt-2 space-y-1 text-xs text-slate-500">
            {returnProjections.map((item) => (
              <li key={item.id}>
                {item.title || ADMIN_DATE_PLACEHOLDER}
                {' · '}
                {adminEnumLabel('admin.properties.scenarioType', item.type, t)}
                {' · '}
                {item.id}
              </li>
            ))}
          </ul>
        ) : null}
      </section>

      {orphaned.length > 0 ? (
        <section className="rounded-xl border border-slate-200 bg-white p-4">
          <h2 className="text-sm font-semibold text-slate-800">
            {t('admin.properties.section.orphanedScenarios')}
          </h2>
          <ul className="mt-3 space-y-2">
            {orphaned.map((item) => (
              <li key={item.id}>
                <CollapsibleScenario
                  scenario={item}
                  ownerUid={ownerUid}
                  t={t}
                  language={language}
                  showFinancials={showFinancials}
                  nested
                />
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </div>
  );
}
